import { Popover, Slider } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { hslToString, parseHsl } from '@/utils/colorConversions';

import { sliderStyles } from './muiStyles';

import s from './ColorPicker.module.scss';

type ColorPickerProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose?: () => void;
  color: string;
  onChange: (color: string) => void;
};

export const ColorPicker: React.FC<ColorPickerProps> = ({
  open,
  anchorEl,
  onClose,
  color,
  onChange,
}) => {
  const initialValues = parseHsl(color);
  const [hue, setHue] = useState(initialValues.h);
  const [saturation, setSaturation] = useState(initialValues.s);
  const [lightness, setLightness] = useState(initialValues.l);
  const hueBarRef = useRef<HTMLDivElement>(null);
  const [isDraggingHue, setIsDraggingHue] = useState(false);
  const lastHueRef = useRef(hue);

  useEffect(() => {
    if (open) {
      const values = parseHsl(color);

      const newHue = values.s === 0 ? lastHueRef.current || 0 : values.h;

      const displaySaturation = values.s === 0 ? 100 : values.s;

      setHue(newHue);
      setSaturation(displaySaturation);
      setLightness(50);

      if (values.s > 0) {
        lastHueRef.current = values.h;
      } else if (lastHueRef.current === undefined || lastHueRef.current === 0) {
        lastHueRef.current = newHue;
      }
    }
  }, [open, color]);

  const handleChange = (newHue: number, newSaturation: number) => {
    const newColor = hslToString(newHue, newSaturation, 50);

    if (newColor !== color) {
      onChange(newColor);
      lastHueRef.current = newHue;
    }
  };

  const handleHueBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!hueBarRef.current) return;

    const rect = hueBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newHue = Math.round(percentage * 360);

    setHue(newHue);
    handleChange(newHue, saturation);
  };

  const handleHueBarMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingHue || !hueBarRef.current) return;

    const rect = hueBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newHue = Math.round(percentage * 360);

    setHue(newHue);
    handleChange(newHue, saturation);
  };

  const handleSaturationChange = (_: Event, value: number | number[]) => {
    const s = Array.isArray(value) ? value[0] : value;
    setSaturation(s);
    handleChange(hue, s);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        '.MuiPaper-root': {
          borderRadius: '12px',
          padding: '16px',
          marginTop: '8px',
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <div className={s.color_picker}>
        <div className={s.color_picker__section}>
          <span className={s.color_picker__label}>Цвет</span>
          <div
            ref={hueBarRef}
            className={s.color_picker__hue_bar}
            onClick={handleHueBarClick}
            onMouseDown={() => setIsDraggingHue(true)}
            onMouseUp={() => setIsDraggingHue(false)}
            onMouseLeave={() => setIsDraggingHue(false)}
            onMouseMove={handleHueBarMouseMove}
          >
            <div
              className={s.color_picker__hue_indicator}
              style={{ left: `${(hue / 360) * 100}%` }}
            />
          </div>
        </div>

        <div className={s.color_picker__section}>
          <span className={s.color_picker__label}>Насыщенность</span>
          <Slider
            value={saturation}
            onChange={handleSaturationChange}
            min={0}
            max={100}
            sx={sliderStyles(hue, saturation, lightness)}
          />
        </div>
      </div>
    </Popover>
  );
};
